import React, { FunctionComponent } from 'react';
import { startCase } from 'lodash';
import { DMSType } from '@upstart/harmony/lib/legos/dmsTypes';
import { DealerLaunchStepProps } from '../types';
import { Dealer } from '../../../types/dealerTypes';
import { Select } from '../../Input/Select/Select';
import { TextField } from '../../Input/TextField/TextField';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { StepInfoGateProps } from './DealerLaunchInfoGate';
import { DMS_TYPE_NAME } from '../../../utils/constants';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import styles from '../styles.module.scss';

export const DealerLaunchIntegrationDealerManagementInfoGate: StepInfoGateProps = {
  title: 'Tell Us About Your DMS Provider',
  cardTitle: 'A quick note about Dealer Management Systems',
  cardDescription:
    'We ask for information about your Dealer Management System (DMS) to better integrate with your business. To do this, Upstart Auto Retail will need your DMS dealer ID. This information will help us build a seamless car-selling experience and determine eligibility for financing products.',
  continueText: 'I Understand',
};

export const DealerLaunchIntegrationDealerManagement: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<Dealer>({ defaultValues: dealer });
  const dmsType = form.watch('dmsType');
  const options: Array<{ label: string; value: string }> = Object.values(
    DMSType,
  ).map((type) => ({
    label: DMS_TYPE_NAME[type] || startCase(type),
    value: type,
  }));

  return (
    <DealerLaunchFormCardLayout
      className={styles.homeStep}
      form={form}
      title={DealerLaunchIntegrationDealerManagementInfoGate.title}
      subTitle="To seamlessly integrate with your business, Upstart Auto Retail needs information about your Dealer Management System (DMS)"
      cardTitle="Which DMS do you use?"
      prevStep={prevStep}
      onFormSubmit={nextStep}
    >
      <div className={styles.stepRow}>
        <Select
          label="DMS Name"
          name="dmsType"
          width="md"
          form={form}
          validation={{ required: true }}
          options={options}
          tooltip="Your DMS is where your dealership manages vehicle inventory, deals, credit reports, and more."
          addEmptyOption
        />
        {(dmsType === DMSType.CDK || dmsType === DMSType.STANDALONE) && (
          <div className={styles.stepRow}>
            <TextField
              label={
                dmsType === DMSType.CDK ? 'CDK Dealer Id' : 'Other DMS name'
              }
              name={
                dmsType === DMSType.CDK ? 'dmsCDK.dealerId' : 'otherDmsName'
              }
              width="md"
              form={form}
              validation={{ required: true }}
            />
          </div>
        )}
      </div>
      <div className={styles.stepRow}>
        {(dmsType === DMSType.CDK || dmsType === DMSType.DT) && (
          <span className={styles.colorInfo}>
            Great news! {DMS_TYPE_NAME[dmsType]} is eligible for Upstart Auto
            Retail Lending.
          </span>
        )}
        {dmsType === DMSType.REYNOLDS && (
          <span className={styles.colorInfo}>
            Currently, {DMS_TYPE_NAME[dmsType]} is not eligible for Upstart Auto
            Retail Lending. We will notify you if this changes.
          </span>
        )}
        {dmsType === DMSType.STANDALONE && (
          <span className={styles.colorInfo}>
            Currently, other DMS providers are not eligible for Upstart Auto
            Retail Lending. We will notify you if this changes.
          </span>
        )}
      </div>
    </DealerLaunchFormCardLayout>
  );
};
